import type { DeliveryWindowOutputRecord, LicensePlate, TireSeason } from '@navio/nsdk';
import type {
  BookingAddress,
  BookingStoreCar,
  InsuranceInfoForCar,
  SelectedSalesItemsCar,
  TireHotelPickupDecision
} from 'noddi-types';

interface AddSalesItemParams extends SelectedSalesItemsCar {
  // SalesItems that are mutually exclusive is auto deselected when new item is added, ex; selecting car wash basic removes car wash premium
  mutuallyExclusiveSalesItemIds: number[];
}

interface RemoveSalesItemParams {
  licensePlate: SelectedSalesItemsCar['licensePlate'];
  salesItemId: SelectedSalesItemsCar['salesItemId'];
  type: SelectedSalesItemsCar['type'];
}

interface RequiredBookingInfo {
  cars: InsuranceInfoForCar[];
}

export interface BookingState {
  clearStore: () => void;

  selectedAddress?: BookingAddress;
  setSelectedAddress: (address?: BookingAddress) => void;

  selectedCars: BookingStoreCar[];
  addCar: (carToAdd: BookingStoreCar) => void;
  removeCar: (carToRemove: BookingStoreCar) => void;

  // Here car + PrimaryItems or Addons or TireHotelPickUpItems are stored
  selectedSalesItemsCars: SelectedSalesItemsCar[];
  // Only used when pre populating the store, no validation is done here as backend is trusted
  setSelectedSalesItemsCars: (selectedSalesItemsCars: SelectedSalesItemsCar[]) => void;

  /** Customer's answer to the backend tire hotel location suggestion (wheel_pickup_location_suggestion). */
  setTireHotelPickupDecision: (decision: TireHotelPickupDecision) => void;

  /** The wheel set that goes into storage for a car signed up for the tire hotel. */
  setIncomingTireSeason: (params: { licensePlate: LicensePlate; season: TireSeason }) => void;

  addSalesItem: (
    params: SelectedSalesItemsCar & {
      // SalesItems that are mutually exclusive is auto deselected when new item is added, ex; selecting car wash basic removes car wash premium
      mutuallyExclusiveSalesItemIds: number[];
    }
  ) => void;
  addSalesItems: (items: AddSalesItemParams[]) => void;
  removeSalesItems: (items: RemoveSalesItemParams[]) => void;
  removeSalesItem: (params: RemoveSalesItemParams) => void;

  selectedTimeWindow?: DeliveryWindowOutputRecord;
  setSelectedTimeWindow: (timeWindow: DeliveryWindowOutputRecord | undefined) => void;

  comment?: string;
  setComment: (comment: string) => void;

  requiredBookingInfo: RequiredBookingInfo;
  setInsuranceCompanyForCar: ({
    licensePlate,
    insuranceCompanyName
  }: {
    licensePlate: LicensePlate;
    insuranceCompanyName?: string;
  }) => void;
  setDateOfDamageForCar: ({ licensePlate, dateOfDamage }: { licensePlate: LicensePlate; dateOfDamage?: Date }) => void;
  setIsGlassDamageCoveredForCar: ({
    licensePlate,
    isGlassDamageCovered
  }: {
    licensePlate: LicensePlate;
    isGlassDamageCovered: boolean;
  }) => void;

  pinLocation?: { lat: number; lng: number };
  setPinLocation: (pinLocation?: { lat: number; lng: number }) => void;

  // always undefined in normal booking flow, unless its prepopulated from a booking proposal, on edit services flow.
  bookingProposalId: undefined | number;
  setBookingProposalId: (bookingProposalId?: number) => void;
  bookingProposalCustomer?: { userGroupId: number; userId: number };
  setBookingProposalCustomer: (customer: BookingState['bookingProposalCustomer']) => void;
}

export interface BookingStoreInitProps {
  storeName: string;
}

// Initial state with empty values
export const initialStateValues = {
  selectedAddress: undefined,
  selectedCars: [],
  selectedSalesItemsCars: [],
  selectedTimeWindow: undefined,
  comment: '',
  requiredBookingInfo: {
    cars: []
  },
  pinLocation: undefined,
  bookingProposalId: undefined,
  bookingProposalCustomer: undefined
};
